import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { CreditCard, ExternalLink, ShieldAlert, Gift, Copy } from "lucide-react";

export default function SettingsBilling() {
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/subscription").catch(() => ({ data: { status: "none", subscription: null } })),
      api.get("/referrals/mine").catch(() => ({ data: null })),
    ]).then(([s, r]) => { setSub(s.data); setRef(r.data); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/portal", { return_url: window.location.origin + "/app/settings/billing" });
      window.location.href = data.portal_url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not open billing portal"); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel subscription at the end of the current period? You keep access until then.")) return;
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Cancellation scheduled at period end.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Cancellation failed"); }
    finally { setBusy(false); }
  };

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "founding_dealer_monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not start checkout"); }
    finally { setBusy(false); }
  };

  const s = sub?.subscription;
  const isActive = sub?.status === "active";
  const nextRenewal = s?.current_period_end ? new Date(s.current_period_end * 1000).toLocaleDateString() : "—";
  const amount = s?.amount ? `$${(s.amount / 100).toFixed(2)} ${s.currency?.toUpperCase()}` : "—";

  return (
    <DashboardLayout>
      <h1 className="text-2xl sm:text-3xl font-bold" data-testid="billing-heading">Billing</h1>
      <p className="mt-1 text-sm text-[#3B424B]">Manage your Founding Dealer subscription.</p>

      {loading ? <div className="mt-6 text-sm text-[#3B424B]">Loading…</div> : (
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <div className="tm-card p-6 bg-white" data-testid="billing-plan-card">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Current plan</div>
              <span className={`tm-badge ${isActive ? "bg-[#14624A]/10 text-[#14624A]" : "bg-[#3B424B]/10 text-[#3B424B]"}`} data-testid="billing-status">{isActive ? "Active" : "No active plan"}</span>
            </div>
            <div className="mt-4 text-2xl font-bold">Founding Dealer</div>
            <div className="mt-1 text-sm text-[#3B424B]">{amount}{s?.interval ? ` / ${s.interval}` : ""}</div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-[11px] uppercase tracking-wider text-[#3B424B] font-semibold">Next renewal</div><div className="mt-1 font-mono" data-testid="billing-next-renewal">{nextRenewal}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-[#3B424B] font-semibold">Cancels at period end</div><div className="mt-1 font-mono">{s?.cancel_at_period_end ? "Yes" : "No"}</div></div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {isActive ? (
                <>
                  <button className="tm-btn-primary" disabled={busy} onClick={openPortal} data-testid="billing-portal"><CreditCard size={15} /> Manage card & invoices <ExternalLink size={12} /></button>
                  {!s?.cancel_at_period_end && (
                    <button className="tm-btn-outline" disabled={busy} onClick={cancel} data-testid="billing-cancel"><ShieldAlert size={15} /> Cancel at period end</button>
                  )}
                </>
              ) : (
                <button className="tm-btn-gold" disabled={busy} onClick={startCheckout} data-testid="billing-subscribe">Subscribe — $99 CAD / month</button>
              )}
            </div>
          </div>
          <div className="tm-card p-6 bg-white">
            <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Included</div>
            <ul className="mt-4 space-y-2 text-sm">
              {["Inventory + POS upload", "Reorder & stockout intelligence", "Slow inventory & pricing signals", "Recommendations with confidence & evidence", "Ask TreadMargin", "Weekly digest email", "Mapping templates"].map((f) => <li key={f}>· {f}</li>)}
            </ul>
            <p className="mt-6 text-xs text-[#3B424B]">Payments are processed by Stripe. Card details never touch TreadMargin's servers. Cancellations take effect at the end of the current billing period.</p>
          </div>
          <div className="tm-card p-6 bg-white" data-testid="referral-card">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-[#14624A] text-white flex items-center justify-center"><Gift size={16} /></div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Refer a dealer</div>
                <div className="font-semibold mt-1">Give one month, get one month</div>
              </div>
            </div>
            <p className="mt-3 text-sm text-[#3B424B]">When a peer dealer you invite subscribes, we credit your account one free month automatically.</p>
            {ref && (
              <>
                <div className="mt-4 flex items-center gap-2">
                  <input readOnly className="tm-input font-mono text-sm" value={`${window.location.origin}/signup?ref=${ref.code}`} data-testid="referral-link" onFocus={(e) => e.target.select()} />
                  <button className="tm-btn-outline h-11 px-3" onClick={async () => {
                    const url = `${window.location.origin}/signup?ref=${ref.code}`;
                    try {
                      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(url); toast.success("Copied."); return; }
                      throw new Error("no-clipboard-api");
                    } catch {
                      const ta = document.createElement("textarea"); ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
                      document.body.appendChild(ta); ta.select();
                      try { document.execCommand("copy"); toast.success("Copied."); }
                      catch { toast.error("Copy failed — select the link and copy manually."); }
                      document.body.removeChild(ta);
                    }
                  }} data-testid="referral-copy"><Copy size={14} /></button>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-center text-xs">
                  <div><div className="text-2xl font-bold font-mono" data-testid="referral-invited">{ref.invited_count}</div><div className="text-[#3B424B] mt-0.5">Signed up</div></div>
                  <div><div className="text-2xl font-bold font-mono" data-testid="referral-pending">{ref.credits_pending}</div><div className="text-[#3B424B] mt-0.5">Pending</div></div>
                  <div><div className="text-2xl font-bold font-mono" data-testid="referral-applied">{ref.credits_applied}</div><div className="text-[#3B424B] mt-0.5">Applied</div></div>
                </div>
              </>
            )}
          </div>

          {isActive && ref && (
            <div className="tm-card p-6 bg-white lg:col-span-2" data-testid="widget-card">
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Trust badge — embed on your site</div>
              <div className="mt-1 font-semibold">Powered by TreadMargin</div>
              <p className="mt-2 text-sm text-[#3B424B]">Copy this snippet to show a small verified-dealer badge on your shop's website. It uses anonymised platform stats only — never your customer data.</p>
              <textarea
                readOnly
                className="mt-4 tm-input font-mono text-xs h-32 resize-y"
                data-testid="widget-snippet"
                onFocus={(e) => e.target.select()}
                value={`<a href="https://treadmargin.com" style="display:inline-flex;align-items:center;gap:8px;padding:8px 14px;border:1px solid #e6e2dc;border-radius:10px;font:600 13px/1 Arial,sans-serif;color:#0F1115;text-decoration:none;background:#FFF"><span style="color:#C9943D">◆</span>TreadMargin Verified Dealer <span style="color:#3B424B;font-weight:400">· ${ref.code}</span></a>`}
              />
              <div className="mt-3 flex flex-wrap gap-3 items-center">
                <div className="rounded-lg border border-[var(--tm-border)] px-3 py-2 inline-flex items-center gap-2 font-semibold text-[13px]" data-testid="widget-preview"><span className="text-[#C9943D]">◆</span> TreadMargin Verified Dealer <span className="text-[#3B424B] font-normal">· {ref.code}</span></div>
                <button className="tm-btn-outline h-9 px-3" onClick={async () => {
                  const snip = document.querySelector('[data-testid="widget-snippet"]').value;
                  try {
                    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(snip); toast.success("Snippet copied."); return; }
                    throw new Error("no-clipboard-api");
                  } catch {
                    const ta = document.querySelector('[data-testid="widget-snippet"]'); ta.select();
                    try { document.execCommand("copy"); toast.success("Snippet copied."); } catch { toast.error("Copy failed — please select manually."); }
                  }
                }} data-testid="widget-copy"><Copy size={14} /> Copy snippet</button>
              </div>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
